import { useProctoredAttempt } from '../../components/focus-guard/FocusGuardContext';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import useContentProtection from '../../hooks/useContentProtection';
import { 
  Keyboard,
  Play,
  Clock,
  Target,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Award,
  ArrowLeft,
  TrendingUp,
  Zap
} from 'lucide-react';

const RPATTypingTests = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Apply content protection - no screenshots, copy, right-click
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testContent, setTestContent] = useState(null);
  const [typedText, setTypedText] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  useProctoredAttempt({ active: isActive, label: 'typing test', onForceEnd: () => submitTest() });
  
  // Track if user came from a lesson (focused mode - only show required test)
  const [fromLesson, setFromLesson] = useState(false);
  
  const textareaRef = useRef(null);
  const timerRef = useRef(null);

  const fetchProgress = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/rpat/typing-tests/progress/summary');
      setProgress(res.data);
    } catch (err) {
      console.error('Failed to fetch typing progress:', err);
      toast.error('Failed to load typing tests');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchProgress();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchProgress]);

  // Handle test query parameter (redirected from lesson)
  // Also reset state when query param is removed (navigating back to test list)
  useEffect(() => {
    const testParam = searchParams.get('test');
    
    if (testParam && progress) {
      // Auto-select test from query param - user came from a lesson
      const testNumber = parseInt(testParam, 10);
      if (testNumber >= 1 && testNumber <= 10 && selectedTest !== testNumber) {
        setFromLesson(true);  // Mark that user came from a lesson
        selectTest(testNumber);
      }
    } else if (!testParam && selectedTest) {
      // Query param removed - reset to test selection view
      setSelectedTest(null);
      setTestContent(null);
      setTypedText('');
      setIsActive(false);
      setStartTime(null);
      setElapsedSeconds(0);
      setResult(null);
      setFromLesson(false);  // Reset fromLesson flag
    }
  }, [searchParams, progress]);

  const selectTest = async (testNumber) => {
    try {
      const res = await api.get(`/rpat/typing-tests/${testNumber}`);
      setTestContent(res.data);
      setSelectedTest(testNumber);
      setTypedText('');
      setIsActive(false);
      setStartTime(null);
      setElapsedSeconds(0);
      setResult(null);
    } catch (err) {
      console.error('Failed to fetch test:', err);
      toast.error('Failed to load typing test');
    }
  };

  const startTest = () => {
    setIsActive(true);
    setStartTime(Date.now());
    setTypedText('');
    textareaRef.current?.focus();
    
    timerRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
  };

  const handleTyping = (e) => {
    if (!isActive) return;
    setTypedText(e.target.value);
  };

  const submitTest = async () => {
    if (!testContent || !startTime) return;
    
    clearInterval(timerRef.current);
    setIsActive(false);
    setIsSubmitting(true);
    
    const timeSeconds = Math.round((Date.now() - startTime) / 1000);
    
    try {
      const res = await api.post(`/rpat/typing-tests/${selectedTest}/submit`, {
        typed_content: typedText,
        time_taken_seconds: timeSeconds
      });
      
      setResult(res.data);
      fetchProgress(); // Refresh progress
    } catch (err) {
      console.error('Failed to submit test:', err);
      toast.error('Failed to submit test');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetTest = () => {
    clearInterval(timerRef.current);
    setTypedText('');
    setIsActive(false);
    setStartTime(null);
    setElapsedSeconds(0);
    setResult(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate real-time WPM
  const calculateWPM = () => {
    if (!isActive || elapsedSeconds === 0) return 0;
    const words = typedText.length / 5;
    const minutes = elapsedSeconds / 60;
    return Math.round(words / minutes);
  };

  // Calculate accuracy highlight
  const getHighlightedText = () => {
    if (!testContent) return '';
    const original = testContent.content;
    
    return original.split('').map((char, i) => {
      if (i >= typedText.length) {
        return { char, status: 'pending' };
      }
      return {
        char,
        status: typedText[i] === char ? 'correct' : 'incorrect'
      };
    });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1,2,3].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </MainLayout>
    );
  }

  // Test Selection View
  if (!selectedTest) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/rpat')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Typing Tests</h1>
              <p className="text-muted-foreground">
                Improve your typing speed and accuracy
              </p>
            </div>
          </div>

          {/* Progress Summary */}
          <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-emerald-100">
                    <Keyboard className="h-8 w-8 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-700">
                      {progress?.tests_passed} / {progress?.total_tests}
                    </p>
                    <p className="text-sm text-emerald-600">Tests Passed</p>
                  </div>
                </div>
                <Progress 
                  value={(progress?.tests_passed / progress?.total_tests) * 100} 
                  className="w-32 h-3"
                />
              </div>
            </CardContent>
          </Card>

          {/* Test Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {progress?.progress?.map((test) => (
              <Card 
                key={test.test_number}
                className={`cursor-pointer transition-all hover:shadow-lg ${
                  test.passed ? 'border-emerald-300 bg-emerald-50/50' : ''
                }`}
                onClick={() => selectTest(test.test_number)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <Badge variant={test.passed ? 'default' : 'secondary'}>
                      Test {test.test_number}
                    </Badge>
                    {test.passed && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                  </div>
                  <h3 className="font-semibold mb-2">{test.title}</h3>
                  
                  {test.passed ? (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-center p-2 bg-white/50 rounded">
                        <p className="font-bold text-cyan-600">{test.best_wpm}</p>
                        <p className="text-xs text-muted-foreground">WPM</p>
                      </div>
                      <div className="text-center p-2 bg-white/50 rounded">
                        <p className="font-bold text-emerald-600">{test.best_accuracy}%</p>
                        <p className="text-xs text-muted-foreground">Accuracy</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {test.attempts} attempts
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </MainLayout>
    );
  }

  // Result View
  if (result) {
    return (
      <MainLayout>
        <div className="p-6 max-w-2xl mx-auto">
          <Card className={`overflow-hidden ${result.passed ? 'border-emerald-500' : 'border-amber-500'}`}>
            <div className={`p-8 text-center text-white ${
              result.passed 
                ? 'bg-gradient-to-r from-emerald-600 to-green-600' 
                : 'bg-gradient-to-r from-amber-600 to-orange-600'
            }`}>
              {result.passed ? (
                <Award className="h-16 w-16 mx-auto mb-4" />
              ) : (
                <RefreshCw className="h-16 w-16 mx-auto mb-4" />
              )}
              <h2 className="text-3xl font-bold mb-2">
                {result.passed ? 'Great Job!' : 'Keep Practicing!'}
              </h2>
              <p className="text-lg opacity-90">{result.feedback}</p>
            </div>
            
            <CardContent className="pt-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-3xl font-bold text-cyan-600">{result.wpm}</p>
                  <p className="text-sm text-muted-foreground">WPM</p>
                  <p className="text-xs text-muted-foreground">
                    (min: {result.min_wpm_required})
                  </p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-3xl font-bold text-emerald-600">{result.accuracy}%</p>
                  <p className="text-sm text-muted-foreground">Accuracy</p>
                  <p className="text-xs text-muted-foreground">
                    (min: {result.min_accuracy_required}%)
                  </p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-3xl font-bold text-violet-600">{result.errors}</p>
                  <p className="text-sm text-muted-foreground">Errors</p>
                </div>
              </div>
              
              <div className="flex gap-3 flex-wrap">
                {result.passed ? (
                  <>
                    <Button 
                      className="flex-1 bg-gradient-to-r from-emerald-600 to-green-600"
                      onClick={() => {
                        // Navigate to next lesson if available, otherwise to associated topic's lesson
                        if (result.next_topic?.id) {
                          toast.success(`Typing test passed! Moving to ${result.next_topic.code || 'next lesson'}`);
                          navigate(`/rpat/lesson/${result.next_topic.id}`);
                        } else if (result.associated_topic_id) {
                          // Go back to the lesson that required this test to show "Completed" state
                          toast.success('Typing test passed! Returning to your lesson.');
                          navigate(`/rpat/lesson/${result.associated_topic_id}`);
                        } else {
                          // Fallback to R-PAT dashboard
                          toast.success('Typing test passed! Continue with your training.');
                          navigate('/rpat');
                        }
                      }}
                    >
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      {result.next_topic?.code ? `Next Lesson: ${result.next_topic.code}` : 'Continue Training'}
                    </Button>
                    <Button 
                      variant="outline"
                      className="flex-1"
                      onClick={resetTest}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Practice Again
                    </Button>
                  </>
                ) : (
                  <>
                    {fromLesson ? (
                      // User came from a lesson - show "Back to Training" instead of "Back to Tests"
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => navigate('/rpat')}
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Training
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          navigate('/rpat/typing-tests', { replace: true });
                        }}
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Tests
                      </Button>
                    )}
                    <Button 
                      className="flex-1"
                      onClick={resetTest}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Try Again
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // Active Test View
  const highlightedChars = getHighlightedText();
  const currentWPM = calculateWPM();

  return (
    <MainLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => {
              // Navigate to clean URL without query params
              navigate('/rpat/typing-tests', { replace: true });
            }}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">{testContent?.title}</h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>{testContent?.word_count} words</span>
                <span>Min {testContent?.min_wpm_to_pass} WPM</span>
                <span>Min {testContent?.min_accuracy_to_pass}% accuracy</span>
              </div>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-cyan-600">{currentWPM}</p>
              <p className="text-xs text-muted-foreground">WPM</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-violet-600">{formatTime(elapsedSeconds)}</p>
              <p className="text-xs text-muted-foreground">Time</p>
            </div>
          </div>
        </div>

        {/* Text to Type */}
        <Card>
          <CardContent className="pt-6">
            <p className="text-lg leading-relaxed font-mono mb-4 p-4 bg-muted/50 rounded-lg">
              {highlightedChars.map((item, i) => (
                <span
                  key={i}
                  className={
                    item.status === 'correct' ? 'text-emerald-600 bg-emerald-100' :
                    item.status === 'incorrect' ? 'text-red-600 bg-red-100' :
                    'text-muted-foreground'
                  }
                >
                  {item.char}
                </span>
              ))}
            </p>
            
            {!isActive ? (
              <div className="text-center">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-cyan-600 to-blue-600"
                  onClick={startTest}
                >
                  <Play className="mr-2 h-5 w-5" />
                  Start Typing
                </Button>
              </div>
            ) : (
              <>
                <Textarea
                  ref={textareaRef}
                  value={typedText}
                  onChange={handleTyping}
                  placeholder="Start typing here..."
                  className="min-h-32 font-mono text-lg"
                  autoFocus
                />
                
                <div className="flex justify-between mt-4">
                  <Button variant="outline" onClick={resetTest}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Reset
                  </Button>
                  <Button 
                    className="bg-gradient-to-r from-emerald-600 to-green-600"
                    onClick={submitTest}
                    disabled={isSubmitting || typedText.length < testContent.content.length * 0.5}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Zap className="h-8 w-8 text-violet-600" />
              <div>
                <p className="font-medium text-violet-800">Typing Tips</p>
                <p className="text-sm text-violet-600">
                  Keep your eyes on the screen, not your keyboard. Focus on accuracy first, speed will follow.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default RPATTypingTests;
